/**
 * AnalyticsSummary — historical analytics extracted from the old
 * DashboardTab. Lives below the operational command-center section on
 * the Tableau page (Phase 5). Collapsed by default so the first screen
 * stays focused on live emergencies.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, BarChart3, Printer, AlertOctagon, ChevronDown, ChevronUp } from "lucide-react";
import { EMERGENCY_TYPES } from "./EmergencyTypeGrid";
import { useT } from "../lib/lang";
import { TimeFilterBar } from "./admin/TimeFilterBar";
import { buildTimeParams, describeTimeSelection } from "../lib/timeFilter";

const Bar = ({ label, value, max, color }) => (
  <div className="flex items-center gap-2">
    <div className="w-24 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 shrink-0 truncate">{label}</div>
    <div className="flex-1 h-4 bg-zinc-800/80 relative overflow-hidden">
      <div
        className="h-full transition-all"
        style={{ width: `${max ? (value / max) * 100 : 0}%`, backgroundColor: color, boxShadow: `inset 0 0 6px ${color}88` }}
      />
    </div>
    <div className="w-8 text-right text-xs font-bold tabular-nums font-mono text-zinc-200">{value}</div>
  </div>
);

const KPI = ({ label, value, sub, color = "text-emerald-300", testid, accent = "border-zinc-800" }) => (
  <div className={`border ${accent} bg-[#0f1318] p-3 relative overflow-hidden`} data-testid={testid}>
    <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-500">{label}</div>
    <div className={`font-display text-3xl font-black tracking-tight mt-1 tabular-nums ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">{sub}</div>}
    <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl from-current opacity-[0.04] pointer-events-none" />
  </div>
);

export const AnalyticsSummary = ({ defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, labels] = useT();
  // Iter80 — Résumé analytique uses the SAME time filter as Rapports.
  const [preset, setPreset] = useState("7d");
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = buildTimeParams(preset, customSince, customUntil);
    api.get("/admin/dashboard", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [open, preset, customSince, customUntil]);

  const exportPdf = () => window.print();
  const typeMax = data ? Math.max(1, ...Object.values(data.by_type || {})) : 1;
  const dailyMax = data ? Math.max(1, ...data.daily.map((d) => d.count)) : 1;
  const falseRate = data && data.total_alerts > 0
    ? Math.round((data.false_total / data.total_alerts) * 100)
    : 0;

  return (
    <section className="border border-zinc-800 bg-[#0b0f14] rounded" data-testid="analytics-summary">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="analytics-summary-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-zinc-900/40 transition-colors rounded"
      >
        <BarChart3 className="w-4 h-4 text-emerald-400" />
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-200">
          Résumé analytique
        </span>
        <span className="text-[10px] text-zinc-500 font-mono ml-2">
          Historique, tendances, modération
        </span>
        <span className="ml-auto text-zinc-500">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="border-t border-zinc-800 px-4 py-4 space-y-4">
          {/* Iter80 — shared TimeFilterBar. Result count + summary
              of the current selection lives below on the KPI strip. */}
          <div className="border border-zinc-800 bg-[#0f1318] p-3" data-testid="analytics-filter-bar">
            <TimeFilterBar
              preset={preset}
              customSince={customSince}
              customUntil={customUntil}
              onChange={({ preset: p, customSince: s, customUntil: u }) => {
                setPreset(p);
                setCustomSince(s ?? "");
                setCustomUntil(u ?? "");
              }}
              testidPrefix="analytics"
            />
            <div className="text-[10px] font-mono text-zinc-500 mt-1.5" data-testid="analytics-period-desc">
              Période : {describeTimeSelection(preset, customSince, customUntil)}
            </div>
          </div>
          {loading || !data ? (
            <div className="flex items-center justify-center py-8 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KPI label="Total alertes" value={data.total_alerts} sub="Tous les temps" testid="kpi-total" />
                <KPI label="7 derniers jours" value={data.last_7_days} sub={`${data.last_30_days} en 30 j`} testid="kpi-7d" color="text-cyan-300" />
                <KPI label="Taux fausses" value={`${falseRate}%`} color="text-rose-400" sub={`${data.false_total} confirmées`} testid="kpi-false" accent="border-rose-500/30" />
                <KPI label="Citoyens enregistrés" value={data.users_total} sub={`${data.users_suspended} suspendus`} testid="kpi-users" color="text-amber-300" />
              </div>

              <div className="border border-zinc-800 bg-[#0f1318] p-4" data-testid="dash-by-type">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 flex items-center gap-1.5 mb-3">
                  <BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Répartition par type
                </div>
                <div className="space-y-2">
                  {EMERGENCY_TYPES.map(({ key, accent }) => (
                    <Bar
                      key={key}
                      label={(labels[key] && labels[key].label) || key}
                      value={data.by_type[key] || 0}
                      max={typeMax}
                      color={accent}
                    />
                  ))}
                </div>
              </div>

              <div className="border border-zinc-800 bg-[#0f1318] p-4" data-testid="dash-trend">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 mb-3">
                  Tendance 7 jours
                </div>
                <div className="flex items-end gap-1.5 h-28">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 relative shadow-[0_0_8px_rgba(16,185,129,0.35)]"
                        style={{ height: `${(d.count / dailyMax) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }}
                      >
                        {d.count > 0 && (
                          <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold tabular-nums font-mono text-zinc-200">
                            {d.count}
                          </span>
                        )}
                      </div>
                      <div className="text-[8px] text-zinc-500 font-mono">{d.day.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-zinc-800 bg-[#0f1318] p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 mb-3 flex items-center gap-1.5">
                  <AlertOctagon className="w-3.5 h-3.5 text-amber-400" /> Modération
                </div>
                <div className="grid grid-cols-3 text-center divide-x divide-zinc-800">
                  <div>
                    <div className="text-2xl font-black font-display text-amber-300 tabular-nums">{data.flagged_total}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500 mt-1">Flagées</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black font-display text-rose-400 tabular-nums">{data.false_total}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500 mt-1">Fausses</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black font-display text-zinc-100 tabular-nums">{data.users_suspect}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500 mt-1">Suspects</div>
                  </div>
                </div>
              </div>

              <button
                onClick={exportPdf}
                data-testid="dash-export-pdf"
                className="w-full border border-emerald-500/50 text-emerald-300 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-emerald-500 hover:text-black transition-colors print:hidden flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" /> Imprimer / Exporter PDF
              </button>

              <div className="text-[10px] text-zinc-600 text-center print:hidden font-mono">
                Généré le {new Date(data.generated_at).toLocaleString("fr-FR")}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default AnalyticsSummary;
